import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, toApiError } from '../api/errors';
import { isAbort } from '../api/http';
import { fetchCurrentUser, logout as logoutRequest } from '../api/endpoints';
import type { CurrentUser } from '../api/types';

export type AuthState =
  | { status: 'checking' }
  | { status: 'authenticated'; user: CurrentUser }
  | { status: 'anonymous' }
  /**
   * 🔴 A FOURTH STATE, ON PURPOSE. The obvious design has three, and treats "the session check
   * failed" as "not signed in" — which shows a login button when the API is simply down. The user
   * then signs in, it fails, and they conclude their account is broken.
   * ⇒ "we could not ask" is not "the answer was no".
   */
  | { status: 'unknown'; error: ApiError };

interface AuthContextValue {
  state: AuthState;
  recheck: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'checking' });
  const [token, setToken] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'checking' });

    fetchCurrentUser(controller.signal)
      .then((user) => {
        if (!mounted.current) return;
        setState({ status: 'authenticated', user });
      })
      .catch((err: unknown) => {
        if (!mounted.current || isAbort(err)) return;
        const apiError = toApiError(err, '/auth/me');
        if (apiError.kind === 'unauthenticated') setState({ status: 'anonymous' });
        else setState({ status: 'unknown', error: apiError });
      });

    return () => controller.abort();
  }, [token]);

  const recheck = useCallback(() => setToken((value) => value + 1), []);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // A failed logout must still clear the local view of the session; the server-side session is
      // revocable independently and leaving the user "signed in" here would be worse.
    }
    if (mounted.current) setState({ status: 'anonymous' });
  }, []);

  const value = useMemo(() => ({ state, recheck, signOut }), [state, recheck, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

export function useCurrentUser(): CurrentUser | null {
  const { state } = useAuth();
  return state.status === 'authenticated' ? state.user : null;
}
